import { useRef, useState, useEffect, useCallback } from "react";
import {
  FiMusic,
  FiImage,
  FiVideo,
  FiUpload,
  FiMic,
  FiTrash2,
  FiEdit2,
  FiCheck,
  FiX,
  FiPlay,
  FiSquare,
  FiPause,
  FiLoader,
  FiScissors,
  FiDownload,
} from "react-icons/fi";

const formatDuration = (secs) => {
  if (!secs || isNaN(secs)) return "0:00.000";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  const ms = Math.floor((secs % 1) * 1000);
  return `${m}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

const formatRecordTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// ── TrimPanel ─────────────────────────────────────────────────────────────
// Inline trim UI — slides in below the asset row
const TrimPanel = ({ asset, onTrim, onClose, trimming }) => {
  const [trimStart, setTrimStart] = useState("");
  const [trimEnd, setTrimEnd] = useState("");
  const [saveOption, setSaveOption] = useState("save");
  const [newName, setNewName] = useState(`${asset.name}_trimmed`);
  const [error, setError] = useState(null);

  const duration = asset.duration || 0;

  const validate = () => {
    const start = parseFloat(trimStart);
    const end = trimEnd !== "" ? parseFloat(trimEnd) : undefined;

    if (trimStart === "" || isNaN(start) || start < 0) {
      setError("Trim start must be a non-negative number");
      return null;
    }
    if (duration > 0 && start >= duration) {
      setError(
        `Trim start must be less than duration (${formatDuration(duration)})`,
      );
      return null;
    }
    if (end !== undefined) {
      if (isNaN(end) || end <= start) {
        setError("Trim end must be greater than trim start");
        return null;
      }
      if (duration > 0 && end > duration) {
        setError(
          `Trim end cannot exceed duration (${formatDuration(duration)})`,
        );
        return null;
      }
    }
    if (saveOption === "saveAs") {
      if (!newName.trim()) {
        setError("New name is required");
        return null;
      }
      if (!/^[a-zA-Z0-9 _-]+$/.test(newName.trim())) {
        setError(
          "Name can only contain letters, numbers, spaces, hyphens, underscores",
        );
        return null;
      }
    }
    setError(null);
    return {
      trimStart: start,
      trimEnd: end,
      saveOption,
      newName: newName.trim(),
    };
  };

  const handleSubmit = () => {
    const payload = validate();
    if (!payload) return;
    onTrim(asset.assetId, payload);
  };

  const previewDuration =
    trimStart !== "" && !isNaN(parseFloat(trimStart))
      ? trimEnd !== "" && !isNaN(parseFloat(trimEnd))
        ? Math.max(0, parseFloat(trimEnd) - parseFloat(trimStart))
        : duration > 0
          ? Math.max(0, duration - parseFloat(trimStart))
          : null
      : null;

  return (
    <div className="preset-trim-panel">
      <div className="preset-trim-header">
        <FiScissors size={10} />
        <span>Trim Audio</span>
        <button
          type="button"
          className="preset-icon-btn"
          onClick={onClose}
          disabled={trimming}
          title="Cancel"
        >
          <FiX size={10} />
        </button>
      </div>

      <div className="preset-trim-fields">
        <div className="preset-trim-field">
          <label className="preset-trim-label">Start (s)</label>
          <input
            className="preset-trim-input"
            type="number"
            min="0"
            step="0.1"
            placeholder="0.0"
            value={trimStart}
            onChange={(e) => {
              setTrimStart(e.target.value);
              setError(null);
            }}
            disabled={trimming}
          />
        </div>
        <div className="preset-trim-field">
          <label className="preset-trim-label">End (s)</label>
          <input
            className="preset-trim-input"
            type="number"
            min="0"
            step="0.1"
            placeholder={duration > 0 ? duration.toFixed(1) : "end"}
            value={trimEnd}
            onChange={(e) => {
              setTrimEnd(e.target.value);
              setError(null);
            }}
            disabled={trimming}
          />
        </div>
        {previewDuration !== null && (
          <div className="preset-trim-preview">
            → {formatDuration(previewDuration)}
          </div>
        )}
      </div>

      <div className="preset-trim-save-options">
        <button
          type="button"
          className={`preset-trim-option ${saveOption === "save" ? "active" : ""}`}
          onClick={() => setSaveOption("save")}
          disabled={trimming}
        >
          Save
        </button>
        <button
          type="button"
          className={`preset-trim-option ${saveOption === "saveAs" ? "active" : ""}`}
          onClick={() => setSaveOption("saveAs")}
          disabled={trimming}
        >
          Save As
        </button>
      </div>

      {saveOption === "saveAs" && (
        <input
          className="preset-rename-input"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            setError(null);
          }}
          placeholder="New asset name…"
          disabled={trimming}
        />
      )}

      {error && <div className="preset-trim-error">{error}</div>}

      <button
        type="button"
        className="preset-trim-submit"
        onClick={handleSubmit}
        disabled={trimming || trimStart === ""}
      >
        {trimming ? (
          <>
            <FiLoader size={9} className="presets-loading-spinner" />
            <span>Trimming…</span>
          </>
        ) : (
          <>
            <FiScissors size={9} />
            <span>
              {saveOption === "saveAs" ? "Trim & Save As" : "Trim & Save"}
            </span>
          </>
        )}
      </button>
    </div>
  );
};

// ── PendingUploadRow ──────────────────────────────────────────────────────
const PendingUploadRow = ({
  pendingUpload,
  uploading,
  onConfirm,
  onCancel,
}) => {
  const [name, setName] = useState(pendingUpload.suggestedName);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    setName(pendingUpload.suggestedName);
  }, [pendingUpload.suggestedName]);

  const handleConfirm = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <div className="preset-asset-row preset-pending-row">
      <div className="preset-pending-label">
        {pendingUpload.type === "audio" && <FiMusic size={11} />}
        {pendingUpload.type === "image" && <FiImage size={11} />}
        {pendingUpload.type === "video" && <FiVideo size={11} />}
      </div>

      <div className="preset-pending-input-row">
        <input
          ref={inputRef}
          className="preset-rename-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
            if (e.key === "Escape") onCancel();
          }}
          disabled={uploading}
          placeholder="Enter a name…"
        />
        <button
          type="button"
          className="preset-icon-btn"
          onClick={handleConfirm}
          disabled={uploading || !name.trim()}
          title="Upload"
        >
          {uploading ? (
            <FiLoader size={10} className="presets-loading-spinner" />
          ) : (
            <FiCheck size={10} />
          )}
        </button>
        <button
          type="button"
          className="preset-icon-btn"
          onClick={onCancel}
          disabled={uploading}
          title="Cancel"
        >
          <FiX size={10} />
        </button>
      </div>

      {pendingUpload.error && (
        <div className="preset-pending-error">{pendingUpload.error}</div>
      )}
    </div>
  );
};

// ── AudioAssetRow ─────────────────────────────────────────────────────────
const AudioAssetRow = ({ asset, onDelete, onRename, onTrim, trimming }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(asset.name);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(asset.duration || 0);
  const [showTrim, setShowTrim] = useState(false);
  const audioRef = useRef(null);
  const rafRef = useRef(null);

  const stopRaf = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const tickScrubber = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      rafRef.current = requestAnimationFrame(tickScrubber);
    }
  }, []);

  useEffect(() => {
    return () => {
      stopRaf();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // If asset URL changed (trim-save), drop stale audio instance so next play uses new file.
  useEffect(() => {
    stopRaf();
    setPlaying(false);
    setCurrentTime(0);
    setDuration(asset.duration || 0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, [asset.url, asset.duration]);

  useEffect(() => {
    setEditName(asset.name);
  }, [asset.name]);

  // Close trim panel when trimming completes
  useEffect(() => {
    if (!trimming && showTrim) {
      // Keep panel open so user sees result — they can close manually
    }
  }, [trimming]);

  const getAudio = () => {
    if (!audioRef.current) {
      const a = new Audio(asset.url);
      a.addEventListener("loadedmetadata", () => setDuration(a.duration));
      a.addEventListener("ended", () => {
        stopRaf();
        setPlaying(false);
        setCurrentTime(a.duration || 0);
      });
      audioRef.current = a;
    }
    return audioRef.current;
  };

  const handlePlay = () => {
    const a = getAudio();
    if (playing) {
      a.pause();
      stopRaf();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
      rafRef.current = requestAnimationFrame(tickScrubber);
    }
  };

  const handleScrub = (e) => {
    const a = getAudio();
    const val = parseFloat(e.target.value);
    a.currentTime = val;
    setCurrentTime(val);
  };

  const handleRenameConfirm = () => {
    if (editName.trim()) onRename(asset.assetId, editName.trim());
    setEditing(false);
  };

  const handleTrim = (assetId, payload) => {
    onTrim(assetId, payload);
  };

  return (
    <div className="preset-asset-row preset-audio-row">
      <div className="preset-audio-top">
        <button
          type="button"
          className={`preset-play-btn ${playing ? "playing" : ""}`}
          onClick={handlePlay}
          title={playing ? "Pause" : "Play"}
        >
          {playing ? <FiPause size={9} /> : <FiPlay size={9} />}
        </button>

        <div className="preset-asset-info">
          {editing ? (
            <input
              className="preset-rename-input"
              value={editName}
              autoFocus
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameConfirm();
                if (e.key === "Escape") setEditing(false);
              }}
            />
          ) : (
            <span className="preset-asset-name" title={asset.name}>
              {asset.name}
            </span>
          )}
          <span className="preset-asset-meta">
            {formatDuration(asset.duration)} · {formatSize(asset.size)}
          </span>
        </div>

        <div className="preset-asset-btns">
          {editing ? (
            <>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={handleRenameConfirm}
                title="Confirm"
              >
                <FiCheck size={10} />
              </button>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={() => setEditing(false)}
                title="Cancel"
              >
                <FiX size={10} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={async () => {
                  const blob = await fetch(asset.url).then((r) => r.blob());
                  const blobUrl = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = blobUrl;
                  a.download = asset.name + ".wav";
                  a.click();
                  URL.revokeObjectURL(blobUrl);
                }}
                title="Download"
              >
                <FiDownload size={10} />
              </button>
              <button
                type="button"
                className={`preset-icon-btn ${showTrim ? "active-trim" : ""}`}
                onClick={() => setShowTrim((v) => !v)}
                title="Trim"
                disabled={trimming}
              >
                <FiScissors size={10} />
              </button>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={() => {
                  setEditName(asset.name);
                  setEditing(true);
                }}
                title="Rename"
              >
                <FiEdit2 size={10} />
              </button>
              <button
                type="button"
                className="preset-icon-btn danger"
                onClick={() => onDelete(asset.assetId)}
                title="Delete"
              >
                <FiTrash2 size={10} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="preset-scrubber-row">
        <span className="preset-scrubber-time">
          {formatDuration(currentTime)}
        </span>
        <input
          type="range"
          className="preset-scrubber"
          min={0}
          max={duration || 1}
          step={0.01}
          value={currentTime}
          onChange={handleScrub}
        />
        <span className="preset-scrubber-time">{formatDuration(duration)}</span>
      </div>

      {showTrim && (
        <TrimPanel
          asset={asset}
          onTrim={(assetId, payload) => {
            handleTrim(assetId, payload);
          }}
          onClose={() => setShowTrim(false)}
          trimming={trimming}
        />
      )}
    </div>
  );
};

// ── ImageAssetRow ─────────────────────────────────────────────────────────
const ImageAssetRow = ({ asset, onDelete, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(asset.name);

  const handleRenameConfirm = () => {
    if (editName.trim()) onRename(asset.assetId, editName.trim());
    setEditing(false);
  };

  return (
    <div className="preset-asset-row">
      <div className="preset-img-thumb">
        <img src={asset.url} alt={asset.name} />
      </div>
      <div className="preset-asset-info">
        {editing ? (
          <input
            className="preset-rename-input"
            value={editName}
            autoFocus
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameConfirm();
              if (e.key === "Escape") setEditing(false);
            }}
          />
        ) : (
          <span className="preset-asset-name" title={asset.name}>
            {asset.name}
          </span>
        )}
        <span className="preset-asset-meta">
          {asset.width}×{asset.height} · {formatSize(asset.size)}
        </span>
      </div>
      <div className="preset-asset-btns">
        {editing ? (
          <>
            <button
              type="button"
              className="preset-icon-btn"
              onClick={handleRenameConfirm}
            >
              <FiCheck size={10} />
            </button>
            <button
              type="button"
              className="preset-icon-btn"
              onClick={() => setEditing(false)}
            >
              <FiX size={10} />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="preset-icon-btn"
              onClick={() => {
                setEditName(asset.name);
                setEditing(true);
              }}
              title="Rename"
            >
              <FiEdit2 size={10} />
            </button>
            <button
              type="button"
              className="preset-icon-btn danger"
              onClick={() => onDelete(asset.assetId)}
              title="Delete"
            >
              <FiTrash2 size={10} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ── VideoAssetRow ─────────────────────────────────────────────────────────
const VideoAssetRow = ({ asset, onDelete, onRename, onTrim, trimming }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(asset.name);
  const [showTrim, setShowTrim] = useState(false);

  const handleRenameConfirm = () => {
    if (editName.trim()) onRename(asset.assetId, editName.trim());
    setEditing(false);
  };

  return (
    <div className="preset-asset-row preset-video-row">
      <div className="preset-video-row-top">
        <div className="preset-video-icon">
          <FiVideo size={12} />
        </div>
        <div className="preset-asset-info">
          {editing ? (
            <input
              className="preset-rename-input"
              value={editName}
              autoFocus
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameConfirm();
                if (e.key === "Escape") setEditing(false);
              }}
            />
          ) : (
            <span className="preset-asset-name" title={asset.name}>
              {asset.name}
            </span>
          )}
          <span className="preset-asset-meta">
            {formatDuration(asset.duration)} · {formatSize(asset.size)}
          </span>
        </div>
        <div className="preset-asset-btns">
          {editing ? (
            <>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={handleRenameConfirm}
              >
                <FiCheck size={10} />
              </button>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={() => setEditing(false)}
              >
                <FiX size={10} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`preset-icon-btn ${showTrim ? "active-trim" : ""}`}
                onClick={() => setShowTrim((v) => !v)}
                title="Trim"
                disabled={trimming}
              >
                <FiScissors size={10} />
              </button>
              <button
                type="button"
                className="preset-icon-btn"
                onClick={() => {
                  setEditName(asset.name);
                  setEditing(true);
                }}
                title="Rename"
              >
                <FiEdit2 size={10} />
              </button>
              <button
                type="button"
                className="preset-icon-btn danger"
                onClick={() => onDelete(asset.assetId)}
                title="Delete"
              >
                <FiTrash2 size={10} />
              </button>
            </>
          )}
        </div>
      </div>

      {showTrim && (
        <TrimPanel
          asset={asset}
          onTrim={onTrim}
          onClose={() => setShowTrim(false)}
          trimming={trimming}
        />
      )}
    </div>
  );
};

// ── PresetsPanel ──────────────────────────────────────────────────────────
const TABS = [
  { key: "audio", label: "Audio", icon: FiMusic },
  { key: "image", label: "Image", icon: FiImage },
  { key: "video", label: "Video", icon: FiVideo },
];

const ACCEPTED = {
  audio: "audio/*",
  image: "image/*",
  video: "video/*",
};

const PresetsPanel = ({
  assets,
  uploading,
  loadingAssets,
  pendingUpload,
  recording,
  recordingTime,
  onAddAudio,
  onAddImage,
  onAddVideo,
  onConfirmUpload,
  onCancelUpload,
  onDelete,
  onRename,
  onTrim, // (assetId, { trimStart, trimEnd, saveOption, newName }) => void
  trimmingId, // assetId currently being trimmed (null if none)
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
}) => {
  const [activeTab, setActiveTab] = useState("audio");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (pendingUpload) setActiveTab(pendingUpload.type);
  }, [pendingUpload]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (activeTab === "audio") await onAddAudio(file);
    else if (activeTab === "image") await onAddImage(file);
    else if (activeTab === "video") await onAddVideo(file);
    e.target.value = "";
  };

  const currentAssets = assets[activeTab] ?? [];
  const isRecording = recording === "recording";
  const isPaused = recording === "paused";
  const isActive = isRecording || isPaused;

  return (
    <div className="presets-panel">
      <div className="animation-workbench-panel-head">
        <h3>Presets</h3>
      </div>

      <div className="presets-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            className={`presets-tab ${activeTab === key ? "active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={11} />
            <span>{label}</span>
            {assets[key]?.length > 0 && (
              <span className="presets-tab-badge">{assets[key].length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="presets-actions">
        <button
          type="button"
          className="animation-workbench-btn presets-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || !!pendingUpload}
        >
          <FiUpload size={11} />
          <span>Upload</span>
        </button>

        {activeTab === "audio" && !isActive && (
          <button
            type="button"
            className="animation-workbench-btn presets-record-btn"
            onClick={onStartRecording}
            disabled={!!pendingUpload}
          >
            <FiMic size={11} />
            <span>Record</span>
          </button>
        )}

        {activeTab === "audio" && isActive && (
          <>
            <button
              type="button"
              className={`animation-workbench-btn presets-record-btn ${isRecording ? "recording" : "paused"}`}
              onClick={isRecording ? onPauseRecording : onResumeRecording}
              title={isRecording ? "Pause" : "Resume"}
            >
              {isRecording ? <FiPause size={11} /> : <FiMic size={11} />}
              <span>{formatRecordTime(recordingTime)}</span>
            </button>
            <button
              type="button"
              className="animation-workbench-btn presets-stop-btn"
              onClick={onStopRecording}
              title="Stop and save"
            >
              <FiSquare size={11} />
            </button>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED[activeTab]}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      <div className="presets-asset-list">
        {pendingUpload && pendingUpload.type === activeTab && (
          <PendingUploadRow
            pendingUpload={pendingUpload}
            uploading={uploading}
            onConfirm={onConfirmUpload}
            onCancel={onCancelUpload}
          />
        )}

        {loadingAssets && currentAssets.length === 0 ? (
          <div className="presets-empty">
            <FiLoader size={20} className="presets-loading-spinner" />
            <p>Loading assets…</p>
          </div>
        ) : currentAssets.length === 0 && !pendingUpload ? (
          <div className="presets-empty">
            {activeTab === "audio" && <FiMusic size={20} />}
            {activeTab === "image" && <FiImage size={20} />}
            {activeTab === "video" && <FiVideo size={20} />}
            <p>No {activeTab} assets</p>
            <span>
              {activeTab === "audio"
                ? "Upload a file or record from mic"
                : `Upload a ${activeTab} file to get started`}
            </span>
          </div>
        ) : (
          currentAssets.map((asset) => {
            if (activeTab === "audio")
              return (
                <AudioAssetRow
                  key={asset.assetId}
                  asset={asset}
                  onDelete={(id) => onDelete("audio", id)}
                  onRename={(id, name) => onRename("audio", id, name)}
                  onTrim={(id, payload) => onTrim("audio", id, payload)}
                  trimming={trimmingId === asset.assetId}
                />
              );
            if (activeTab === "image")
              return (
                <ImageAssetRow
                  key={asset.assetId}
                  asset={asset}
                  onDelete={(id) => onDelete("image", id)}
                  onRename={(id, name) => onRename("image", id, name)}
                />
              );
            return (
              <VideoAssetRow
                key={asset.assetId}
                asset={asset}
                onDelete={(id) => onDelete("video", id)}
                onRename={(id, name) => onRename("video", id, name)}
                onTrim={(id, payload) => onTrim("video", id, payload)}
                trimming={trimmingId === asset.assetId}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default PresetsPanel;
